import { submitServerChange } from 'components/dataCreationForms/common';
import { areObjectIdsEqual } from 'helpers/idComparison';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import {
  ComponentType,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useProjectId, useProjectTimezone } from 'store/project';
import { useCurrentUserInProjectLabelled } from 'store/user';
import { levelClient } from 'shared/domain/level/levelClient';
import { createLevelOnViewToCreateModel } from 'shared/domain/level/mapping/toModel';
import { CreateLevelOnView } from 'shared/domain/level/types/view';
import { nowTimezonedDateTime } from 'shared/utils/date/dates';
import { createInitialLevelFormData } from 'views/level/model';
import {
  CreateLevelContext,
  OpenCreateLevelDialog,
  OpenCreateLevelDialogParams,
  PreselectedMapDisplayData,
} from '../types';
import { handleFormValidation, validateLevel } from '../validation';
import { getLevelsStoreState } from 'store/level';
import { useSites } from 'store/site';

const SUBMIT_EVENT_TYPE = 'submit-level';
const WithCreateLevelContext = createContext<
  CreateLevelContext | undefined
>(undefined);
const formName = 'level';

function WithCreateLevel({
  children,
  onSuccessCallback,
}: {
  children: ReactElement;
  onSuccessCallback?: (_id: string, label: string) => void;
}): ReactElement {
  const {
    items: sites,
  } = useSites();

  const [isPosting, setIsPosting] = useState<boolean>(false);
  const projectId = useProjectId();
  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const currentUserLabelled = useCurrentUserInProjectLabelled();

  const timezone = useProjectTimezone();

  const initialLevelFormData = useMemo(
    () =>
      createInitialLevelFormData(
        nowTimezonedDateTime(timezone),
        currentUserLabelled
      ),
    [timezone, currentUserLabelled]
  );

  const [initialValues, setInitialValues] = useState<CreateLevelOnView>(
    initialLevelFormData
  );

  const [open, setOpen] = useState(false);
  const [preselectedMapDisplay, setPreselectedMapDisplay] = useState<
    PreselectedMapDisplayData | undefined
  >(undefined);

  const openDialog: OpenCreateLevelDialog = useCallback(
    ({
      siteId,
      freeSoloLabel,
      preselectedMap,
    }: OpenCreateLevelDialogParams) => {
      let currentValues: CreateLevelOnView = {
        ...initialLevelFormData,
      };

      const currentSite = sites.find((s) => areObjectIdsEqual(s._id, siteId));
      if (currentSite) {
        currentValues = {
          ...currentValues,
          site: currentSite,
        };
      }

      if (freeSoloLabel) {
        currentValues = {
          ...currentValues,
          label: freeSoloLabel,
        };
      }

      if (preselectedMap) {
        const { documentationId, localId, name } = preselectedMap;
        currentValues = {
          ...currentValues,
          map: { documentationId },
        };
        setPreselectedMapDisplay({ documentationId, localId, name });
      } else {
        setPreselectedMapDisplay(undefined);
      }

      setInitialValues(currentValues);
      setOpen(true);
    },
    [initialLevelFormData, sites]
  );
  const onSuccess = useCallback(
    (_id, label) => onSuccessCallback?.(_id, label),
    [onSuccessCallback]
  );
  const confirmCancel = useCancelConfirmation(formName);
  const openConfirmationDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setOpen(false);
  }, [confirmCancel]);

  const submitForm = useCallback(
    async (values: CreateLevelOnView) => {
      const levelCreateModel = createLevelOnViewToCreateModel(values, projectId);
      const { saved } = await submitServerChange({
        change: () => levelClient.create(levelCreateModel),
        setOpen,
        setIsPosting,
        action: 'creating a level',
      });
      if (!saved) {
        return;
      }

      const createdLevel = (await getLevelsStoreState().actions.reload())?.find(
        (level) => level.label === values.label && areObjectIdsEqual(level.site, values.site?._id)
      );
      if (createdLevel) {
        onSuccess(createdLevel._id, createdLevel.label);
      }
    },
    [projectId, onSuccess]
  );

  const ctx: CreateLevelContext = useMemo(() => {
    return {
      open,
      openDialog,
      closeDialog: openConfirmationDialog,
      submitForm,
      isPosting,
      initialValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate: validateLevel,
      handleFormValidation,
      onSuccess,
      formName,
      preselectedMapDisplay,
    };
  }, [
    openConfirmationDialog,
    initialValues,
    isPosting,
    open,
    openDialog,
    releaseSubmitEvent,
    submitForm,
    onSuccess,
    preselectedMapDisplay,
  ]);

  return (
    <WithCreateLevelContext.Provider value={ctx}>
      {children}
    </WithCreateLevelContext.Provider>
  );
}

function useCreateLevel(): CreateLevelContext {
  const context = useContext(WithCreateLevelContext);
  if (context === undefined) {
    throw new Error(
      'useCreateLevel must be used within an CreateLevelContext'
    );
  }
  return context;
}

const withCreateLevel =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithCreateLevel>
      <Component {...props} />
    </WithCreateLevel>
  );

export { WithCreateLevel, useCreateLevel, withCreateLevel };
